import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useState, Suspense, lazy } from "react";
import CookieConsent from "@/components/CookieConsent";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { TenantProvider } from "@/context/TenantContext";
import { trackPageView } from "@/lib/analytics";
import ProtectedRoute, { RoleGuard } from "@/components/ProtectedRoute";
import Sidebar, { MobileBottomNav } from "@/components/Sidebar";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useTenant } from "@/context/TenantContext";

// ── Lazy-loaded pages (loaded on demand, not at app boot) ──────────────────
const Dashboard      = lazy(() => import("@/pages/Dashboard"));
const Movements      = lazy(() => import("@/pages/Movements"));
const Stock          = lazy(() => import("@/pages/Stock"));
const Orders         = lazy(() => import("@/pages/Orders"));
const Suppliers      = lazy(() => import("@/pages/Suppliers"));
const Inventory      = lazy(() => import("@/pages/Inventory"));
const Settings       = lazy(() => import("@/pages/Settings"));
const Users          = lazy(() => import("@/pages/Users"));
const Categories     = lazy(() => import("@/pages/Categories"));
const ExpiryForecast = lazy(() => import("@/pages/ExpiryForecast"));
const Finances       = lazy(() => import("@/pages/Finances"));
const Billing        = lazy(() => import("@/pages/Billing"));
const SuperAdmin     = lazy(() => import("@/pages/SuperAdmin"));
const Register       = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword  = lazy(() => import("@/pages/ResetPassword"));
const PrivacyPolicy  = lazy(() => import("@/pages/legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("@/pages/legal/TermsOfService"));
const LegalNotice    = lazy(() => import("@/pages/legal/LegalNotice"));

// ── Shell ───────────────────────────────────────────────────────────────────
function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[color:var(--bg)]">
      <Sidebar />
      <MobileBottomNav />
      <main className="md:pl-[240px] pb-24 md:pb-0">
        <div className="max-w-[1400px] mx-auto p-5 md:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}

function ProtectedShell({ children }) {
  return (
    <ProtectedRoute>
      <RoleGuard>
        <Shell>{children}</Shell>
      </RoleGuard>
    </ProtectedRoute>
  );
}

// ── Lazy page wrapper with lightweight skeleton ─────────────────────────────
function PageSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-8 bg-[color:var(--border-soft)] rounded w-1/3" />
      <div className="h-4 bg-[color:var(--border-soft)] rounded w-1/2" />
      <div className="h-64 bg-[color:var(--border-soft)] rounded-xl mt-6" />
    </div>
  );
}

// ── HomeRouter ──────────────────────────────────────────────────────────────
function HomeRouter() {
  const tenant = useTenant();
  if (tenant?.isRootDomain) return <Landing />;
  return (
    <ProtectedShell>
      <Dashboard />
    </ProtectedShell>
  );
}

// ── Page tracker ────────────────────────────────────────────────────────────
function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname, document.title);
  }, [location.pathname]);
  return null;
}

// ── App ─────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <ErrorBoundary>
    <BrowserRouter>
      <PageTracker />
      <TenantProvider>
      <AuthProvider>
        {/* Suspense: each route chunk loads only when visited */}
        <Suspense fallback={<PageSkeleton />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login"          element={<Login />} />
            <Route path="/register"       element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy"        element={<PrivacyPolicy />} />
            <Route path="/terms"          element={<TermsOfService />} />
            <Route path="/legal"          element={<LegalNotice />} />

            {/* Home: Landing on root, Dashboard on tenant subdomain */}
            <Route path="/"              element={<HomeRouter />} />
            <Route path="/landing"       element={<Landing />} />

            {/* Protected: lazy-loaded */}
            <Route path="/mouvements"    element={<ProtectedShell><Movements /></ProtectedShell>} />
            <Route path="/stock"         element={<ProtectedShell><Stock /></ProtectedShell>} />
            <Route path="/inventaire"    element={<ProtectedShell><Inventory /></ProtectedShell>} />
            <Route path="/commandes"     element={<ProtectedShell><Orders /></ProtectedShell>} />
            <Route path="/peremptions"   element={<ProtectedShell><ExpiryForecast /></ProtectedShell>} />
            <Route path="/finances"      element={<ProtectedShell><Finances /></ProtectedShell>} />
            <Route path="/fournisseurs"  element={<ProtectedShell><Suppliers /></ProtectedShell>} />
            <Route path="/categories"    element={<ProtectedShell><Categories /></ProtectedShell>} />
            <Route path="/parametres"    element={<ProtectedShell><Settings /></ProtectedShell>} />
            <Route path="/abonnement"    element={<ProtectedShell><Billing /></ProtectedShell>} />
            <Route path="/utilisateurs"  element={<ProtectedShell><Users /></ProtectedShell>} />
            <Route path="/super-admin"   element={<ProtectedShell><SuperAdmin /></ProtectedShell>} />
          </Routes>
        </Suspense>
        <Toaster position="top-right" richColors closeButton />
        <CookieConsent />
      </AuthProvider>
      </TenantProvider>
    </BrowserRouter>
    </ErrorBoundary>
  );
}